import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { UserPlus, Loader2, CheckCircle, ArrowLeft, Info, Upload, X as XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { registerTradie, getRegions, getCategories, uploadLogo } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const RegisterPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    description: '',
    regions: [],
    categories: [],
    acceptedTerms: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [regionsData, categoriesData] = await Promise.all([
          getRegions(),
          getCategories()
        ]);
        setRegions(regionsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to load options:', error);
      }
    };
    loadOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleRegionToggle = (region) => {
    setFormData(prev => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter(r => r !== region)
        : [...prev.regions, region]
    }));
    if (errors.regions) {
      setErrors({ ...errors, regions: null });
    }
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
    if (errors.categories) {
      setErrors({ ...errors, categories: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
    if (!formData.contact_name.trim()) newErrors.contact_name = 'Contact name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email address';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.regions.length === 0) newErrors.regions = 'Select at least one region';
    if (formData.categories.length === 0) newErrors.categories = 'Select at least one service category';
    if (!formData.acceptedTerms) newErrors.acceptedTerms = 'You must accept the Terms and Conditions';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please use a PNG, JPG, WEBP or GIF image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be smaller than 5MB');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    try {
      let logo_url = null;
      if (logoFile) {
        setLogoUploading(true);
        try {
          const result = await uploadLogo(logoFile);
          logo_url = result.logo_url;
        } catch (err) {
          toast.error('Failed to upload logo. You can add it later from your portal.');
        } finally {
          setLogoUploading(false);
        }
      }
      const payload = { ...formData };
      if (logo_url) payload.logo_url = logo_url;
      await registerTradie(payload);
      setSubmitted(true);
      trackEvent('tradie_register_submit', {
        regions_count: payload.regions?.length || 0,
        categories_count: payload.categories?.length || 0,
        has_logo: !!logo_url,
      });
      toast.success('Registration submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit registration';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="register-success">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-secondary" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mb-2">Registration Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for your interest. Our team will review your application and contact you within 2-3 business days.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Once approved, you'll receive instructions to complete your subscription payment ($49 NZD/month) to activate your listing.
            </p>
            <Link to="/">
              <Button className="bg-primary hover:bg-primary/90">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="register-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Home
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
            List Your Business
          </h1>
          <p className="text-muted-foreground">
            Join our directory of verified healthy homes tradies in New Zealand
          </p>
        </div>

        <Alert className="mb-8 bg-accent border-accent">
          <Info className="h-4 w-4 text-primary" />
          <AlertDescription className="text-accent-foreground">
            <strong>$49 NZD per month</strong> subscription fee applies after your application is approved. 
            Your listing will be visible to homeowners searching for healthy homes services across New Zealand.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Registration Form
            </CardTitle>
            <CardDescription>
              Fill in your details below. All fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
              {/* Business Details */}
              <div className="space-y-4">
                <h3 className="font-heading font-semibold text-lg border-b pb-2">Business Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleChange}
                      placeholder="ABC Insulation Ltd"
                      className={errors.business_name ? 'border-destructive' : ''}
                      data-testid="reg-business-name"
                    />
                    {errors.business_name && <p className="text-xs text-destructive">{errors.business_name}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className={errors.contact_name ? 'border-destructive' : ''}
                      data-testid="reg-contact-name"
                    />
                    {errors.contact_name && <p className="text-xs text-destructive">{errors.contact_name}</p>}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className={errors.email ? 'border-destructive' : ''}
                      data-testid="reg-email"
                    />
                    {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="021 123 4567"
                      className={errors.phone ? 'border-destructive' : ''}
                      data-testid="reg-phone"
                    />
                    {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="website">Website (optional)</Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="www.example.com"
                    data-testid="reg-website"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Business Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Tell homeowners about your services, experience, and what makes you the right choice..."
                    rows={4}
                    className={errors.description ? 'border-destructive' : ''}
                    data-testid="reg-description"
                  />
                  {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
                </div>

                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label>Business Logo (optional)</Label>
                  <p className="text-xs text-muted-foreground">PNG, JPG, WEBP or GIF. Max 5MB. Recommended: square image.</p>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-stone-200 bg-white flex-shrink-0">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                          data-testid="reg-logo-preview"
                        />
                        <button
                          type="button"
                          onClick={clearLogo}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center hover:bg-destructive/90"
                          aria-label="Remove logo"
                          data-testid="reg-logo-remove"
                        >
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 flex items-center justify-center flex-shrink-0">
                        <Upload className="w-6 h-6 text-stone-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                        data-testid="reg-logo-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        data-testid="reg-logo-choose"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {logoFile ? 'Change Logo' : 'Choose Logo'}
                      </Button>
                      {logoFile && (
                        <p className="text-xs text-muted-foreground mt-2 truncate">{logoFile.name}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Categories */}
              <div className="space-y-4">
                <h3 className="font-heading font-semibold text-lg border-b pb-2">Service Categories *</h3>
                <p className="text-sm text-muted-foreground">Select all services you provide</p>
                {errors.categories && <p className="text-xs text-destructive">{errors.categories}</p>}
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="categories-checkboxes">
                  {categories.map((category) => (
                    <label 
                      key={category}
                      htmlFor={`cat-${category}`}
                      className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.categories.includes(category) 
                          ? 'border-primary bg-primary/5' 
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <Checkbox
                        id={`cat-${category}`}
                        checked={formData.categories.includes(category)}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      />
                      <span className="text-sm flex-1">
                        {category}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Regions */}
              <div className="space-y-4">
                <h3 className="font-heading font-semibold text-lg border-b pb-2">Service Regions *</h3>
                <p className="text-sm text-muted-foreground">Select all regions where you provide services</p>
                {errors.regions && <p className="text-xs text-destructive">{errors.regions}</p>}
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="regions-checkboxes">
                  {regions.map((region) => (
                    <label 
                      key={region}
                      htmlFor={`reg-${region}`}
                      className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.regions.includes(region) 
                          ? 'border-primary bg-primary/5' 
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <Checkbox
                        id={`reg-${region}`}
                        checked={formData.regions.includes(region)}
                        onCheckedChange={() => handleRegionToggle(region)}
                      />
                      <span className="text-sm flex-1">
                        {region}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="space-y-4 pt-4 border-t">
                <div 
                  className={`flex items-start space-x-3 p-4 rounded-lg border transition-colors ${
                    formData.acceptedTerms 
                      ? 'border-primary bg-primary/5' 
                      : errors.acceptedTerms 
                        ? 'border-destructive bg-destructive/5'
                        : 'border-stone-200'
                  }`}
                >
                  <Checkbox
                    id="acceptTerms"
                    checked={formData.acceptedTerms}
                    onCheckedChange={(checked) => {
                      setFormData({ ...formData, acceptedTerms: !!checked });
                      if (errors.acceptedTerms) setErrors({ ...errors, acceptedTerms: null });
                    }}
                    className="mt-0.5"
                    data-testid="reg-accept-terms"
                  />
                  <div className="flex-1">
                    <label htmlFor="acceptTerms" className="text-sm cursor-pointer">
                      I have read and agree to the{' '}
                      <Link 
                        to="/terms" 
                        target="_blank" 
                        className="text-primary hover:underline font-medium"
                      >
                        Terms and Conditions
                      </Link>
                      {' '}and{' '}
                      <Link 
                        to="/privacy" 
                        target="_blank" 
                        className="text-primary hover:underline font-medium"
                      >
                        Privacy Policy
                      </Link>
                      {' *'}
                    </label>
                  </div>
                </div>
                {errors.acceptedTerms && <p className="text-xs text-destructive">{errors.acceptedTerms}</p>}
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 h-12 text-base"
                  disabled={loading}
                  data-testid="reg-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Submit Registration
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
